import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Editor } from 'primereact/editor';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';

import { useEmployee } from '../../../context/employee';
import { useSite } from '../../../context/site';
import EmployeeDropdown from '../../employee-dropdown';
import ServiceTypeProgramDropdown from '../../service-type-program-dropdown';
import { GetEditorFormats } from '../../../shared';

function ServiceProgramCreate({ open, onClose }) {
    const { employee } = useEmployee();
    const { SearchPrograms, CreateServiceProgram } = useSite();

    const [dateService, setDateService] = useState(new Date());
    const [employeeId, setEmployeeId] = useState('');
    const [program, setProgram] = useState('');
    const [filteredPrograms, setFilteredPrograms] = useState([]);
    const [serviceType, setServiceType] = useState('');
    const [notes, setNotes] = useState('');

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const onSearchProgram = (data) => {
        SearchPrograms({ query: data.query }, (res) => {
            setFilteredPrograms(res.data.programs);
        });
    };

    const buttonDisabled = () => {
        if (!dateService) {
            return true;
        }

        if (!employeeId) {
            return true;
        }

        if (!program) {
            return true;
        }

        if (!serviceType) {
            return true;
        }

        return false;
    };

    const onCancel = () => {
        onClose({ status: 499, message: 'Canceled' });
    };

    const onCreate = () => {
        setProcessing(true);

        const createData = {
            dateService,
            employee: employeeId,
            program,
            serviceType,
            notes
        };

        CreateServiceProgram(createData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                onClose(res);
            }
        });
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Create"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onCreate}
            />
        </div>
    );

    const programItemTemplate = (item) => {
        return (
            <div className="flex align-items-center">
                <div>
                    {item.programName}, {item.siteCity}, {item.siteState.itemData.abbr}
                </div>
            </div>
        );
    };

    // Set employee
    useEffect(() => {
        setEmployeeId(employee?._id ? employee._id : '');
    }, [employee]);

    return (
        <Dialog
            header="Create New Program Service"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            <div className="mt-2">
                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="dateServiceId" className="font-bold block mb-2">
                            Service Date
                        </label>

                        <Calendar
                            inputId="dateServiceId"
                            value={dateService}
                            onChange={(e) => setDateService(e.value)}
                            showIcon
                            showButtonBar
                            appendTo="self"
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="employeeId" className="font-bold block mb-2">
                            Employee
                        </label>

                        <EmployeeDropdown
                            employeeId={employeeId}
                            onChange={(employeeId) => setEmployeeId(employeeId)}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                            Service Type
                        </label>

                        <ServiceTypeProgramDropdown
                            serviceTypeProgramId={serviceType}
                            onChange={(serviceTypeId) => {
                                setServiceType(serviceTypeId);
                            }}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                            Program
                        </label>

                        <AutoComplete
                            field="programName"
                            value={program}
                            suggestions={filteredPrograms}
                            completeMethod={onSearchProgram}
                            onChange={(e) => {
                                setProgram(e.value);
                            }}
                            itemTemplate={programItemTemplate}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="notesId" className="font-bold block mb-2">
                            Notes
                        </label>

                        <Editor
                            id="notesId"
                            value={notes}
                            headerTemplate={<GetEditorFormats />}
                            onTextChange={(e) => setNotes(e.htmlValue)}
                            style={{ height: '320px' }}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Creating program service...</p>

            <ProgressSpinner />
        </>
    );
}

export default ServiceProgramCreate;
